// ✅ src/pages/VehicleV2/BranchwiseVehicleReportPrint.jsx  (FULL FILE)
// Prints Pivot report (Branchwise Master Report) with the SAME template + UI style
// used in your OwnEquipmentReport print page.
//
// API used:
//   GET https://nissi-india.com/api/branchwiseMasterReport/branchwisePivot
//   params: vehmode=O|H, vehtype, month, year, status
//
// How to open (recommended):
// navigate("/vehicle/BranchwiseVehicleReportPrint", { state: { vehmode, month, year, status, vehtype } })
//
// Fallback (direct open):
// /vehicle/BranchwiseVehicleReportPrint?vehmode=O&month=&year=&status=%25&vehtype=

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const API_BASE = "https://nissi-india.com/api/branchwiseMasterReport";

const toStr = (v) => String(v ?? "").trim();

const pick = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
};

// ✅ safer number display for pivot cells
const toCell = (v) => {
  if (v === null || v === undefined || v === "") return "0";
  const s = String(v).trim();
  if (!s) return "0";
  return s;
};

export default function BranchwiseVehicleReportPrint() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ get params from navigate state
  const stateParams = location?.state || {};

  // ✅ fallback: query string
  const queryParams = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      vehmode: p.get("vehmode"),
      vehtype: p.get("vehtype"),
      month: p.get("month"),
      year: p.get("year"),
      status: p.get("status"),
    };
  }, []);

  const filters = useMemo(() => {
    const vehmode = pick(stateParams.vehmode, queryParams.vehmode, "O");
    const vehtype = pick(stateParams.vehtype, queryParams.vehtype, "");
    const month = pick(stateParams.month, queryParams.month, "");
    const year = pick(stateParams.year, queryParams.year, "");
    const status = pick(stateParams.status, queryParams.status, "%");

    // status may come as "%25" if user typed it manually.
    const decodedStatus = decodeURIComponent(status);

    return {
      vehmode: vehmode === "H" ? "H" : "O",
      vehtype: vehtype || "",
      month: month || "",
      year: year || "",
      status: decodedStatus || "%",
    };
  }, [stateParams, queryParams]);

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [rows, setRows] = useState([]);

  // ✅ dynamic pivot columns (branch names etc)
  const columns = useMemo(() => {
    if (!rows?.length) return [];

    const keys = Object.keys(rows[0] || {});

    // keep these first if present
    const fixed = [
      "fld_vehicletypeindex",
      "VehicleTypes",
      "VehicleType",
      "total",
      "typetotal",
      "fld_vehicletypeid",
      "fld_vehiclemaintypeid",
    ].filter((k) => keys.includes(k));

    const others = keys.filter((k) => !fixed.includes(k));

    // If your SP returns both VehicleTypes and VehicleType,
    // VehicleTypes is usually the “master type label”, VehicleType is the raw type label.
    return [...fixed, ...others];
  }, [rows]);

  const titleText = useMemo(() => {
    const modeText = filters.vehmode === "H" ? "Hire Vehicle" : "Own Vehicle";
    const mon = filters.month ? filters.month : "All Months";
    const yr = filters.year ? filters.year : "All Years";
    return `${modeText} - ${mon} / ${yr}`;
  }, [filters]);

  const fetchPivot = async () => {
    try {
      setLoading(true);
      setErrMsg("");

      const { data } = await axios.get(`${API_BASE}/listing`, {
        params: {
          vehmode: filters.vehmode,
          vehtype: filters.vehtype,
          month: filters.month,
          year: filters.year,
          status: filters.status, // "%" or 0/1
        },
      });

      if (!data?.success) throw new Error(data?.message || "Failed to load report");

      const list = Array.isArray(data.rows) ? data.rows : [];
      setRows(list);

      if (!list.length) toast.info("No Records Found");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load report";
      setErrMsg(msg);
      setRows([]);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPivot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* ✅ Print Styles (same style pattern as your template) */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 10px;
            font-size: 11pt;
          }
          .no-print {
            display: none !important;
          }
          table {
            border-collapse: collapse;
          }
          th,
          td {
            border: 1px solid #000 !important;
            padding: 3px !important;
          }
          .print-portrait {
            size: A4 portrait;
            margin: 0.5in;
          }
        }
        @page {
          size: A4 portrait;
          margin: 0.5in;
        }
      `}</style>

      {/* Top Bar (no-print) */}
      <div className="no-print flex items-center justify-between p-3 border-b">
        <div className="text-sm">
          <span className="font-semibold">Mode:</span> {filters.vehmode === "H" ? "Hire" : "Own"}
          <span className="ml-3 font-semibold">Month:</span> {filters.month || "All"}
          <span className="ml-3 font-semibold">Year:</span> {filters.year || "All"}
          <span className="ml-3 font-semibold">Status:</span> {filters.status}
          {loading && <span className="ml-3">Loading...</span>}
          {errMsg && <span className="ml-3 text-red-600">{errMsg}</span>}
        </div>

        <div className="flex gap-2">
          <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            disabled={loading}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div className="p-4 print:p-2 print-portrait max-w-6xl mx-auto">
        <div className="border-2 border-black">
          {/* Header Section */}
          <div className="border-b-2 border-black">
            <div className="flex items-center p-3">
              <div className="w-24 h-20 border-2 border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
                <div className="text-center">
                  <div className="text-xs">NISSI</div>
                  <div className="text-xs">INFOTECH</div>
                  <div className="text-xs">PVT LTD</div>
                </div>
              </div>

              <div className="flex-1 text-center">
                <h1 className="text-xl font-bold">Nissi Infotech</h1>
                <div className="text-sm font-semibold">Nissi</div>
                <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
                <div className="text-xs mt-1">
                  3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                </div>
                <div className="text-xs">
                  Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  infracnsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          {/* Report Title */}
          <div className="text-center py-2 border-b-2 border-black bg-gray-100">
            <h2 className="text-lg font-bold">Branchwise Vehicle Report</h2>
            <div className="text-xs font-semibold mt-1">{titleText}</div>
          </div>

          {/* Filter Summary Row */}
          <div className="border-b-2 border-black">
            <div className="grid grid-cols-2 text-sm">
              <div className="border-r border-black p-2">
                <div className="flex">
                  <span className="font-semibold w-40">Vehicle Mode</span>
                  <span className="mx-2">:</span>
                  <span>{filters.vehmode === "H" ? "Hire Vehicle" : "Own Vehicle"}</span>
                </div>
              </div>
              <div className="p-2">
                <div className="flex">
                  <span className="font-semibold w-40">Main Type Ids</span>
                  <span className="mx-2">:</span>
                  <span>{filters.vehtype || "All"}</span>
                </div>
              </div>
            </div>

            <div className="border-t border-black grid grid-cols-2 text-sm">
              <div className="border-r border-black p-2">
                <div className="flex">
                  <span className="font-semibold w-40">Month / Year</span>
                  <span className="mx-2">:</span>
                  <span>
                    {filters.month || "All"} / {filters.year || "All"}
                  </span>
                </div>
              </div>
              <div className="p-2">
                <div className="flex">
                  <span className="font-semibold w-40">Status</span>
                  <span className="mx-2">:</span>
                  <span>{filters.status}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pivot Grid */}
          <div className="p-2">
            {loading ? (
              <div className="p-6 text-center text-sm">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-sm">No Records Found</div>
            ) : (
              <div style={{ width: "100%", overflow: "auto" }}>
                <table className="w-full table-auto">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="text-xs font-bold text-center">S.No</th>
                      {columns.map((c) => (
                        <th key={c} className="text-xs font-bold text-center whitespace-nowrap">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx}>
                        <td className="text-xs text-center">{idx + 1}</td>
                        {columns.map((c) => (
                          <td key={c} className="text-xs text-center whitespace-nowrap">
                            {toCell(r?.[c])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t-2 border-black p-2 text-xs flex justify-between items-center">
            <div>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</div>
            <div className="font-bold">1</div>
          </div>
        </div>
      </div>
    </div>
  );
}
